import { useRef, useState } from "react";
import { X, Upload, Check, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Avatar, AVATAR_PRESETS } from "./Avatar";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

async function resizeImageToDataURL(file, maxDim = 320, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function AvatarPickerSheet({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(user?.avatar_url || null);
  const fileRef = useRef(null);

  if (!open) return null;

  const save = async (value) => {
    setSaving(true);
    try {
      await api.patch("/users/me/avatar", { avatar_url: value || "" });
      await refresh();
      toast.success("Avatar atualizado!");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 5MB)");
      return;
    }
    try {
      const dataUrl = await resizeImageToDataURL(f);
      setPreview(dataUrl);
    } catch {
      toast.error("Não foi possível ler a imagem");
    }
  };

  const previewUser = { ...user, avatar_url: preview };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="phone-frame w-full" onClick={(e) => e.stopPropagation()}>
        <div className="neon-card-violet mx-3 mb-3 p-6 slide-up max-h-[85vh] overflow-y-auto" data-testid="avatar-picker-sheet">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="chip chip-pink mb-2">🎨 Seu avatar</p>
              <h2 className="font-arcade text-2xl font-bold text-white">Personalizar</h2>
            </div>
            <button onClick={onClose} data-testid="close-avatar-picker" className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-300">
              <X size={20} />
            </button>
          </div>

          <div className="flex items-center justify-center mb-6">
            <Avatar user={previewUser} size={120} ringClass="avatar-glow" />
          </div>

          <p className="text-xs font-mono text-slate-400 uppercase tracking-widest mb-3">Suba uma foto</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="avatar-file-input"
            onChange={handleFile}
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="avatar-upload-btn"
            className="btn-cyan w-full mb-5 flex items-center justify-center gap-2"
          >
            <Upload size={18} /> Escolher foto da galeria
          </button>

          <p className="text-xs font-mono text-slate-400 uppercase tracking-widest mb-3">Ou escolha um estilo</p>
          <div className="grid grid-cols-4 gap-3 mb-5" data-testid="avatar-presets">
            {AVATAR_PRESETS.map((p) => {
              const active = preview === `preset:${p.id}`;
              return (
                <button
                  key={p.id}
                  data-testid={`avatar-preset-${p.id}`}
                  onClick={() => setPreview(`preset:${p.id}`)}
                  className={`relative rounded-full p-0.5 transition-transform ${
                    active ? "ring-2 ring-fuchsia-400 scale-105" : ""
                  }`}
                >
                  <div className={`w-full aspect-square rounded-full bg-gradient-to-br ${p.gradient} flex items-center justify-center`}>
                    <span className="font-arcade text-white font-bold text-xl">
                      {(user?.name || "?")[0]?.toUpperCase()}
                    </span>
                  </div>
                  {active && (
                    <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-fuchsia-500 flex items-center justify-center shadow-lg">
                      <Check size={12} className="text-white" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex gap-2">
            {user?.avatar_url && (
              <button
                onClick={() => save("")}
                disabled={saving}
                data-testid="avatar-remove-btn"
                className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 font-arcade font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Trash2 size={16} /> Remover
              </button>
            )}
            <button
              onClick={() => save(preview)}
              disabled={saving || !preview || preview === user?.avatar_url}
              data-testid="avatar-save-btn"
              className="flex-[2] btn-arcade disabled:opacity-50"
            >
              {saving ? "Salvando..." : "Salvar avatar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
